import type { FieldContent } from "@capture/shared";
import { useFormConfigStore } from "../../state/store";
import { useLookupTables, type LookupTableSummary } from "../../../hooks/useLookupTables";
import { FieldRow } from "./Field";

/**
 * Lookup configuration editor — picks a tenant `LookupTable` and the
 * display + return columns for `dataType=lookup` fields. Renders only
 * when the dispatcher (TypeSpecificSection) selects this branch.
 *
 * Data source — `useLookupTables()` wraps `GET /api/lookup-tables`,
 * which returns each table with a `columns: string[]` schema. That
 * column list populates the display/return dropdowns directly, so we
 * don't need a second per-table fetch to know what columns exist.
 *
 * Persistence — every interaction commits `{ lookupTableId,
 * lookupDisplayColumn, lookupReturnColumn }` to the store via
 * `updateField`. Switching tables clears stale columns that don't
 * exist on the new table (e.g. moving from a Vendors table with an
 * `address` column to a Cost Codes table without one) so the editor
 * never persists an invalid column reference.
 *
 * Empty state — if the tenant has zero lookup tables, the section
 * shows a hint pointing the operator at the Lookup Tables admin page.
 *
 * Error state — when the fetch fails (network/5xx), we render a
 * "Couldn't load…" surface with a Retry button rather than falling
 * through to the empty-state copy (a transient failure misread as
 * "no tables defined" was a quality-review finding).
 */

export function LookupConfigSection() {
  const content = useFormConfigStore((s) => s.content);
  const selectedFieldName = useFormConfigStore((s) => s.selectedFieldName);
  const updateField = useFormConfigStore((s) => s.updateField);
  const { tables, isLoading, error, refresh } = useLookupTables();

  const field = content?.fields.find((f) => f.name === selectedFieldName) ?? null;
  if (!field || !content) return null;

  const selectedTable: LookupTableSummary | undefined = tables.find(
    (t) => t.id === field.lookupTableId,
  );
  const columns = selectedTable?.columns ?? [];

  function commitTable(nextId: string) {
    if (!field) return;
    if (nextId === "") {
      // "Unset" — clear all three lookup fields.
      updateField(field.name, {
        lookupTableId: undefined,
        lookupDisplayColumn: undefined,
        lookupReturnColumn: undefined,
      });
      return;
    }
    const next = tables.find((t) => t.id === nextId);
    if (!next) return;
    // Preserve display/return columns ONLY if they still exist on the
    // new table; otherwise drop them so the persisted shape stays valid.
    const patch: Partial<FieldContent> = { lookupTableId: nextId };
    patch.lookupDisplayColumn = next.columns.includes(field.lookupDisplayColumn ?? "")
      ? field.lookupDisplayColumn
      : undefined;
    patch.lookupReturnColumn = next.columns.includes(field.lookupReturnColumn ?? "")
      ? field.lookupReturnColumn
      : undefined;
    updateField(field.name, patch);
  }

  function commitDisplayColumn(next: string) {
    if (!field) return;
    updateField(field.name, { lookupDisplayColumn: next === "" ? undefined : next });
  }

  function commitReturnColumn(next: string) {
    if (!field) return;
    updateField(field.name, { lookupReturnColumn: next === "" ? undefined : next });
  }

  if (isLoading) {
    return (
      <section className="field-section" aria-label="Lookup Configuration">
        <h3 className="field-section__title">Lookup Configuration</h3>
        <p className="field-section__hint">Loading lookup tables…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="field-section" aria-label="Lookup Configuration">
        <h3 className="field-section__title">Lookup Configuration</h3>
        <p className="field-section__error" role="alert">
          Couldn't load lookup tables — {error}
        </p>
        <button
          type="button"
          className="field-section__button"
          onClick={() => {
            void refresh();
          }}
        >
          Retry
        </button>
      </section>
    );
  }

  if (tables.length === 0) {
    return (
      <section className="field-section" aria-label="Lookup Configuration">
        <h3 className="field-section__title">Lookup Configuration</h3>
        <p className="field-section__hint">
          No lookup tables defined for this tenant. Create one in the Lookup
          Tables admin to enable this field.
        </p>
      </section>
    );
  }

  return (
    <section className="field-section" aria-label="Lookup Configuration">
      <h3 className="field-section__title">Lookup Configuration</h3>

      <FieldRow
        htmlFor="lookup-table"
        label="Lookup table"
        hint="Tenant table the operator picks from."
      >
        <select
          id="lookup-table"
          className="field-section__input"
          value={field.lookupTableId ?? ""}
          onChange={(e) => commitTable(e.target.value)}
        >
          <option value="">— select a table —</option>
          {tables.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </FieldRow>

      <FieldRow
        htmlFor="lookup-display"
        label="Display column"
        hint="Column shown to the operator in the picker."
      >
        <select
          id="lookup-display"
          className="field-section__input"
          value={field.lookupDisplayColumn ?? ""}
          onChange={(e) => commitDisplayColumn(e.target.value)}
          disabled={!selectedTable}
        >
          <option value="">— select a column —</option>
          {columns.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </FieldRow>

      <FieldRow
        htmlFor="lookup-return"
        label="Return column"
        hint="Column whose value is persisted to this field."
      >
        <select
          id="lookup-return"
          className="field-section__input"
          value={field.lookupReturnColumn ?? ""}
          onChange={(e) => commitReturnColumn(e.target.value)}
          disabled={!selectedTable}
        >
          <option value="">— select a column —</option>
          {columns.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </FieldRow>
    </section>
  );
}
